import { Observable, Subscribable, SubscribableOrNullableValue } from "knockout";
import WCCAnimator from "managers/animator_new";

interface WCCSliderResizerContext {
    height: number
    prevHeight: number
    heightChange: number
    scroll: boolean
}

export interface WCCSliderResizerConfig {
    element: HTMLElement
    trackHeight: Observable<number | undefined>

    height: SubscribableOrNullableValue<number>    
}

export default class WCCSliderResizer extends WCCAnimator<WCCSliderResizerContext> {
    private prevHeight = ko.observable<number>();

    private element: HTMLElement
    private trackHeight: Observable<number | undefined>

    private height: Subscribable<number | undefined>    

    constructor(config: WCCSliderResizerConfig) {
        super();

        this.element = config.element;
        this.trackHeight = config.trackHeight;

        this.height = ko.flattenComputed(config.height);        

        queueMicrotask(() => {
            this.effects.register([
                this.trackHeight.subscribe(trackHeight => this.prevHeight(trackHeight)),
                this.height.subscribe(() => this.update())
            ]);
        });
    }

    protected override checkIfAllowedToAnimate() {
        const height = this.height();
        const prevHeight = this.prevHeight();

        return super.checkIfAllowedToAnimate() &&
            height != undefined &&
            height > 0 &&
            prevHeight != undefined &&
            prevHeight > 0;
    }

    protected getAnimationDuration() {
        return 500;
    }

    protected getAnimationContext() {
        const height = this.height() ?? 0;
        const prevHeight = this.prevHeight() ?? 0;
        const heightChange = height - prevHeight;

        const rect = this.element.getBoundingClientRect();
        const scroll = rect.top < 0;

        return {
            height: height,
            prevHeight: prevHeight,
            heightChange: heightChange,
            scroll: scroll
        }
    }

    protected override onProgress(progress: number, context: WCCSliderResizerContext) {
        super.onProgress(progress, context);

        const {
            prevHeight,
            heightChange: totalHeightChange,
            scroll
        } = context;

        const currentHeight = this.trackHeight() ?? 0;
        const newHeight = prevHeight + totalHeightChange * progress / 100;
        const heightChange = newHeight - currentHeight;
        const scrollPos = document.documentElement.scrollTop;

        if (progress > 0 && scroll)
            document.documentElement.scrollTop = scrollPos + heightChange;

        this.trackHeight(newHeight);
    }
}